import { useEffect, useState } from 'react';
import type { Place } from './geo';
import PlaceSearch from './PlaceSearch';
import { STATUSES } from './status';
import type { Driver, DriverPatch } from './types';

type Props = {
    driver: Driver,
    parked: boolean,
    placeLabel: string,
    onSave: (patch: DriverPatch, newTruckNumber?: string) => void,
    onMoveTo: (place: Place) => void,
    onPark: () => void,
    onDelete: () => void,
};

// A text field that keeps what is being typed and saves it on blur or Enter.
function Field({ label, value, multiline, onCommit }: { label: string, value: string, multiline?: boolean, onCommit: (value: string) => void }) {
    const [text, setText] = useState(value);
    useEffect(() => setText(value), [value]);
    const commit = () => {
        if (text !== value) {
            onCommit(text);
        }
    };
    const shared = {
        value: text,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setText(event.target.value),
        onBlur: commit,
    };
    return (
        <label className='field'>
            <span>{label}</span>
            {multiline
                ? <textarea rows={2} {...shared} />
                : <input {...shared} onKeyDown={(event) => { if (event.key === 'Enter') { event.currentTarget.blur(); } }} />}
        </label>
    );
}

export default function DriverEditor({ driver, parked, placeLabel, onSave, onMoveTo, onPark, onDelete }: Props) {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    useEffect(() => setConfirmingDelete(false), [driver.truckNumber]);

    return (
        <div className='editor' onClick={(event) => event.stopPropagation()}>
            <Field label='Truck and driver' value={driver.truckNumber} onCommit={(value) => { if (value.trim()) { onSave({}, value); } }} />
            <div className='field-row'>
                <Field label='Dimensions' value={driver.vehicleDimensions || ''} onCommit={(vehicleDimensions) => onSave({ vehicleDimensions })} />
                <Field label='Phone' value={driver.phoneNumber || ''} onCommit={(phoneNumber) => onSave({ phoneNumber })} />
            </div>
            <div className='field'>
                <span>Status</span>
                <div className='segments'>
                    {STATUSES.map((status) => <button
                        key={status.value}
                        className={driver.available === status.value ? 'on' : ''}
                        style={{ '--accent': status.color } as React.CSSProperties}
                        onClick={() => onSave({ available: status.value })}
                    >{status.label}</button>)}
                </div>
            </div>
            <div className='field'>
                <span>Location · {parked ? 'parked' : placeLabel}</span>
                <PlaceSearch placeholder={parked ? 'Put on the map at ZIP, city or address' : 'Move to ZIP, city or address'} onPick={onMoveTo} />
            </div>
            <div className='editor-actions'>
                {!parked && <button onClick={onPark} title='Keep the driver, take the pin off the map'>Park</button>}
                {driver.phoneNumber && <a className='button' href={`tel:${driver.phoneNumber.replace(/[^\d+]/g, '')}`}>Call</a>}
                <span className='spacer' />
                {confirmingDelete
                    ? <>
                        <span className='confirm-text'>Delete this driver?</span>
                        <button className='danger' onClick={onDelete}>Delete</button>
                        <button onClick={() => setConfirmingDelete(false)}>Keep</button>
                    </>
                    : <button className='quiet-danger' onClick={() => setConfirmingDelete(true)}>Delete</button>}
            </div>
            {driver.updatedBy && driver.lastChanged && <div className='editor-meta'>
                Last changed by {driver.updatedBy} · {new Date(driver.lastChanged).toLocaleString()}
            </div>}
        </div>
    );
}
